// Fichier: src/pages/accounting/AccountLedgerIndex.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, IconButton, Chip, TextField, MenuItem,
  InputAdornment, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Search as SearchIcon } from '@mui/icons-material';
import { db } from '../../firebase';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';

const CLASSES = [1, 2, 3, 4, 5, 6, 7];

const AccountLedgerIndex = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');

  useEffect(() => {
    const unsubAccounts = onSnapshot(query(collection(db, 'accounting_accounts'), orderBy('code')), (snap) => {
      setAccounts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    const unsubEntries = onSnapshot(
      query(collection(db, 'accounting_entries'), where('status', '==', 'validé')),
      (snap) => {
        setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }
    );
    return () => {
      unsubAccounts();
      unsubEntries();
    };
  }, []);

  const years = useMemo(() => {
    const set = new Set(entries.map((e) => e.exerciceId).filter(Boolean));
    return Array.from(set).sort().reverse();
  }, [entries]);

  const filteredEntries = useMemo(() => {
    if (yearFilter === 'all') return entries;
    return entries.filter((e) => e.exerciceId === yearFilter);
  }, [entries, yearFilter]);

  const balances = useMemo(() => {
    const map = {};
    filteredEntries.forEach((e) => {
      (e.lines || []).forEach((l) => {
        if (!map[l.accountId]) map[l.accountId] = { debit: 0, credit: 0, count: 0 };
        map[l.accountId].debit += Number(l.debit) || 0;
        map[l.accountId].credit += Number(l.credit) || 0;
        map[l.accountId].count += 1;
      });
    });
    return map;
  }, [filteredEntries]);

  const rows = useMemo(() => {
    return accounts
      .map((a) => {
        const m = balances[a.id] || { debit: 0, credit: 0, count: 0 };
        const solde = a.normalSide === 'credit' ? m.credit - m.debit : m.debit - m.credit;
        return { ...a, totalDebit: m.debit, totalCredit: m.credit, count: m.count, solde };
      })
      .filter((a) => {
        const matchClass = classFilter === 'all' || Number(a.classNumber) === Number(classFilter);
        const s = search.trim().toLowerCase();
        const matchSearch = !s || a.code?.toLowerCase().includes(s) || a.label?.toLowerCase().includes(s);
        return matchClass && matchSearch;
      });
  }, [accounts, balances, classFilter, search]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/accounting')} aria-label="Retour">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Grand livre</Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Rechercher un code ou un libellé..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 260 }}
        />
        <TextField select size="small" label="Exercice" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="all">Toutes années</MenuItem>
          {years.map((y) => (
            <MenuItem key={y} value={y}>{y}</MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup value={classFilter} exclusive size="small" onChange={(e, val) => val !== null && setClassFilter(val)}>
          <ToggleButton value="all">Toutes</ToggleButton>
          {CLASSES.map((c) => (
            <ToggleButton key={c} value={c}>{`Cl. ${c}`}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Code</strong></TableCell>
              <TableCell><strong>Libellé</strong></TableCell>
              <TableCell align="right"><strong>Mouvements</strong></TableCell>
              <TableCell align="right"><strong>Débit</strong></TableCell>
              <TableCell align="right"><strong>Crédit</strong></TableCell>
              <TableCell align="right"><strong>Solde</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Aucun compte pour ces filtres.
                </TableCell>
              </TableRow>
            )}
            {rows.map((a) => (
              <TableRow
                key={a.id}
                hover
                onClick={() => navigate(`/accounting/ledger/${a.id}${yearFilter !== 'all' ? `?year=${yearFilter}` : ''}`)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell sx={{ fontFamily: 'monospace' }}>{a.code}</TableCell>
                <TableCell>{a.label}</TableCell>
                <TableCell align="right">
                  {a.count > 0 ? <Chip size="small" label={a.count} /> : <Typography variant="caption" color="text.secondary">—</Typography>}
                </TableCell>
                <TableCell align="right">{a.totalDebit.toFixed(2)} €</TableCell>
                <TableCell align="right">{a.totalCredit.toFixed(2)} €</TableCell>
                <TableCell align="right">
                  <strong>{a.solde.toFixed(2)} €</strong>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default AccountLedgerIndex;
